<template>
    <div class="content">
        <div class="md-layout">
            <div class="md-layout-item">
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <OrderFilterMenu
                            v-if="!showCarrierOrders || activeTab !== 'Booked to Carrier'"
                            @handleFilterChange="handleFilterChange"
                        />
                        <ManifestTableFilters
                            v-if="showCarrierOrders && activeTab === 'Booked to Carrier'"
                            :initial-from-date="manifestFromDate"
                            :initial-to-date="manifestToDate"
                            @filter-changed="handleManifestFilterChange"
                        />
                    </div>
                    <div class="custom-toolbar-end">
                        <search-component
                            v-if="$root.isDesktop && (!showCarrierOrders || activeTab !== 'Booked to Carrier')"
                            :search-filter="currentSearchTextValue"
                            :handle-search-on-type="true"
                            @onSearch="handleFilterOrSearch"
                        />
                    </div>
                </div>
                <md-card class="custom-margin-bottom">
                    <md-card-header class="md-card-header-icon md-card-header-warning">
                        <div class="card-icon">
                            <md-icon>receipt_long</md-icon>
                        </div>
                    </md-card-header>
                    <orders-table-header-actions
                        :selected-order-ids="selectedOrders.map((x) => x.orderId)"
                        :top-scroll-position="topScrollPosition"
                        :actions-to-display="tableHeaderActionsToDisplay"
                        @scheduling-link-button-clicked="sendSchedulingLink"
                        @ready-for-packing-button-clicked="handleReadyForPackingClicked"
                        @batch-assign-clicked="handleBatchAssignClicked"
                        @book-now-button-clicked="handleBookNowClicked"
                    />
                    <template v-if="!showCarrierOrders">
                        <orders-table
                            :order-list="orderList"
                            :is-list-loading="isListLoading"
                            :show-carrier-orders="showCarrierOrders"
                            :selected-orders="selectedOrders"
                            :column-dropdown-options="columnDropdownOptions"
                            :selected-column="selectedColumn"
                            :is-disabled="isDisabled"
                            :table-body-actions-to-display="tableBodyActionsToDisplay"
                            :can-assign-carrier="canAssignCarrier"
                            :loading-orders="loadingOrders"
                            :order-rates-fetch-status="orderRatesFetchStatus"
                            @select-all-changed="onSelectAll"
                            @single-checkbox-changed="singleCheckbox"
                            @column-selected="handleSelectedColumn"
                            @assigned-carrier-changed="handleAssignedCarrierChanged"
                            @update-order="updateOrder"
                            @delete-order="handleDelete"
                            @put-order-on-hold="putOrderOnHold"
                            @cancel-order="cancelOrder"
                            @download-label-clicked="handleDownloadLabelButtonClicked"
                        />
                    </template>
                    <md-card-content class="body-list" v-else>
                        <tabs
                            :tab-name="tabNames"
                            class="custom-tab-list"
                            color-button="success"
                            @switch-panel="switchTab"
                            :selected-panel="activeTab"
                        >
                            <template v-for="(tabName, index) in tabNames" :slot="`tab-pane-${index + 1}`">
                                <template v-if="isListLoading">
                                    <skeleton-table :key="index"></skeleton-table>
                                </template>
                                <template v-else>
                                    <template v-if="activeTab === 'Booked to Carrier'">
                                        <carrier-manifests-table
                                            :manifests="manifests"
                                            :key="index"
                                            @download-labels="handleDownloadLabelsForManifest"
                                        ></carrier-manifests-table>
                                    </template>
                                    <orders-table
                                        v-else
                                        :key="tabName"
                                        :order-list="orderList"
                                        :show-carrier-orders="showCarrierOrders"
                                        :selected-orders="selectedOrders"
                                        :column-dropdown-options="columnDropdownOptions"
                                        :selected-column="selectedColumn"
                                        :is-disabled="isDisabled"
                                        :table-body-actions-to-display="tableBodyActionsToDisplay"
                                        :can-assign-carrier="canAssignCarrier"
                                        :loading-orders="loadingOrders"
                                        :order-rates-fetch-status="orderRatesFetchStatus"
                                        @select-all-changed="onSelectAll"
                                        @single-checkbox-changed="singleCheckbox"
                                        @column-selected="handleSelectedColumn"
                                        @assigned-carrier-changed="handleAssignedCarrierChanged"
                                        @update-order="updateOrder"
                                        @put-order-on-hold="putOrderOnHold"
                                        @cancel-order="cancelOrder"
                                        @download-label-clicked="handleDownloadLabelButtonClicked"
                                    />
                                </template>
                            </template>
                        </tabs>
                    </md-card-content>
                </md-card>
                <md-card-actions class="page-footer" md-alignment="space-between">
                    <div>
                        <p v-if="total === pagination.perPage" class="card-category">
                            Page {{ pagination.currentPage }} of many
                        </p>
                        <p v-else class="card-category">Page {{ pagination.currentPage }} of {{ totalPages }}</p>
                    </div>
                    <pagination
                        v-model="pagination.currentPage"
                        class="pagination-no-border pagination-success"
                        :per-page="pagination.perPage"
                        :total="total"
                        @change-page="handleChangePage($event, pagination.perPage)"
                    />
                </md-card-actions>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { createCancellationTokenSource, handleRequests, showErrorMessage } from '@/helpers';
import { PAGINATION_DEFAULTS } from '@/utils/defaults';
import { Pagination, SearchComponent } from '@/components';
import { CarrierMarketplaceMixin, GeneralMixin, OrderMixin } from '@/mixins';
import _ from 'lodash';
import { DATE_TYPES, FEATURE_NAMES } from '@/utils/constants';
import moment from 'moment';
import { FeatureManager } from '@/directives';
import AssignOrderToCarrierModal from '@/pages/Orders/components/AssignOrderToCarrierModal';
import OrdersTableHeaderActions from '@/pages/Orders/components/OrdersTableHeaderActions';
import CarrierManifestsTable from '@/pages/Orders/components/CarrierManifestsTable';
import SkeletonTable from '@/pages/Orders/components/SkeletonTable';
import BookOrdersToCarrierModal from '@/pages/Orders/components/BookOrdersToCarrierModal';
import Tabs from '@/components/Tabs';
import OrderFilterMenu from './OrderFilterMenu';
import SendBulkSms from './SendBulkSms';
import UpdateOrderModal from './UpdateOrderModal';
import OrdersTable from './OrdersTable';
import ManifestTableFilters from './ManifestTableFilters';

const TABS = [
    {
        label: 'New Orders',
        value: 'newOrders'
    },
    {
        label: 'In Preparation',
        value: 'inPreparation'
    },
    {
        label: 'Booked to Carrier',
        value: 'bookedToCarrier'
    }
];
export default {
    name: 'OrdersDashboard',
    mixins: [GeneralMixin, OrderMixin, CarrierMarketplaceMixin, FeatureManager],
    components: {
        SkeletonTable,
        CarrierManifestsTable,
        Tabs,
        OrdersTableHeaderActions,
        Pagination,
        SearchComponent,
        OrderFilterMenu,
        OrdersTable,
        ManifestTableFilters
    },
    props: {
        showCarrierOrders: {
            type: Boolean,
            default: () => false
        }
    },
    data() {
        return {
            featureNames: FEATURE_NAMES,
            orderList: [],
            /**
             * Should contain an array of numbers (Order IDs) to keep track
             * of any orders that are being processed in the background an need to show a loading indicator
             * of any orders that are being processed in the background and need to show a loading indicator
             */
            loadingOrders: [],
            /**
             * State machine way of keep track of a status
             * Diagram
             * |>IDLE <-> PENDING
             * Starts as Idle, when a request is made it goes to PENDING
             * When the request is completed it goes back to IDLE
             */
            orderRatesFetchStatus: 'idle',
            activeTab: TABS[0].label,
            isListLoading: false,
            pagination: PAGINATION_DEFAULTS,
            columnDropdownOptions: [
                { key: 'order-number', name: 'Order #' },
                { key: 'source-reference', name: 'Source Ref' }
            ],
            selectedColumn: { key: 'order-number', name: 'Order #' },
            isDisabled: false,
            currentSearchTextValue: '',
            customerIdFilterValue: null,
            fromDate: moment().format(DATE_TYPES.internationalDate),
            toDate: moment().format(DATE_TYPES.internationalDate),
            dateFilterType: 'orderDate',
            isSelectAll: false,
            selectedOrders: [],
            topScrollPosition: 0,
            orderStatus: 'Created',
            rateGroupId: null,

            /**
             * @type {import("axios").CancelTokenSource}
             */
            fetchOrdersCancellationTokenSource: null,
            /**
             * @type {import("axios").CancelTokenSource}
             */
            fetchManifestsCancellationTokenSource: null,
            manifests: [],

            // Manifest-specific date filters (default: yesterday to 7 days after yesterday)
            manifestFromDate: moment()
                .subtract(1, 'days')
                .toDate(),
            manifestToDate: moment()
                .subtract(1, 'days')
                .add(7, 'days')
                .toDate()
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            isReadOnlyUser: 'user/isReadOnlyUser'
        }),
        currentPageMax() {
            const highBound = this.currentPageMin + this.pagination.perPage;
            return this.total < highBound ? this.total : highBound;
        },
        currentPageMin() {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        total() {
            return this.pagination.total;
        },
        totalPages() {
            if (this.total > 0) {
                return Math.ceil(this.total / this.pagination.perPage);
            }
            return 1;
        },
        /**
         *
         * @returns {string[]}
         */
        tableHeaderActionsToDisplay() {
            /**
             * logic to show actions
             * all selected orders inPreparationDate === null, ready for packing
             * all selected orders inPreparationDate !== null, book now
             * all selected orders carrierBookedDate !== null, download manifest
             */

            const allSelectedOrdersHaveCarrier = this.selectedOrders.every(
                (order) => order.assignedCarrier !== null && order.assignedCarrier.teamCarrierId !== null
            );
            const actions = ['sendSchedulingLink'];
            if (!this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace)) {
                return actions;
            }

            if (allSelectedOrdersHaveCarrier) {
                const showReadyForPacking = this.selectedOrders.every(
                    (order) => order.assignedCarrier.inPreparationDate === null
                );
                const showBookNow = this.selectedOrders.every(
                    (order) =>
                        order.assignedCarrier.inPreparationDate !== null &&
                        order.assignedCarrier.carrierBookedDate === null
                );
                const showDownloadManifest = this.selectedOrders.every(
                    (order) => order.assignedCarrier.carrierBookedDate !== null
                );
                if (showReadyForPacking) {
                    actions.push('readyForPacking');
                } else if (showBookNow) {
                    actions.push('bookNow');
                } else if (showDownloadManifest) {
                    actions.push('downloadManifest');
                }
            } else {
                actions.push('batchAssign');
            }

            return actions;
        },
        tableBodyActionsToDisplay() {
            const actions = ['edit', 'putOnHold', 'cancel', 'delete'];
            if (this.activeTab === 'In Preparation') {
                actions.push(...['downloadLabel', 'bookToCarrier']);
            }
            return actions;
        },
        tabNames() {
            return TABS.map((tab) => tab.label);
        },
        canAssignCarrier() {
            return this.activeTab === 'New Orders';
        }
    },
    async mounted() {
        await this.fetchOrdersList();
        this.createHandleScroll();
    },
    beforeDestroy() {
        document.querySelector('.main-panel').removeEventListener('scroll', this.handleScroll);
    },
    watch: {
        async activeTab(newTab) {
            await this.loadRecords({
                newTab
            });
        }
    },
    methods: {
        async loadRecords({ newTab, currentPage = 1, perPage = 50 }) {
            this.selectedOrders = [];
            if (newTab === 'Booked to Carrier') {
                await this.fetchManifests(currentPage, perPage);
            } else {
                await this.fetchOrdersList(currentPage, perPage);
            }
        },
        async fetchManifests(pageNumber = 1, itemsPerPage = 50) {
            if (this.fetchManifestsCancellationTokenSource !== null) {
                this.fetchManifestsCancellationTokenSource.cancel('Operation cancelled by the user.');
                this.fetchManifestsCancellationTokenSource = null;
            }
            this.fetchManifestsCancellationTokenSource = createCancellationTokenSource();
            try {
                this.isListLoading = true;
                const endpoint = `/api/carrier-jobs/manifests`;
                const query = {
                    pageNumber,
                    pageSize: itemsPerPage
                };

                // Add date filters if they are set
                if (this.manifestFromDate) {
                    query.fromDate = moment(this.manifestFromDate).format('YYYY-MM-DD');
                }
                if (this.manifestToDate) {
                    query.toDate = moment(this.manifestToDate).format('YYYY-MM-DD');
                }

                const response = await handleRequests(
                    endpoint,
                    {
                        params: query
                    },
                    true,
                    this.fetchManifestsCancellationTokenSource.token
                );
                this.manifests = response.data.manifests;
            } catch (e) {
                const message = 'Error in fetching manifests';
                showErrorMessage(this, message, e);
            } finally {
                this.isListLoading = false;
                this.fetchManifestsCancellationTokenSource = null;
            }
        },
        /**
         *
         * @param {{ carrierJobManifestId: number }} manifest
         * @returns {Promise<void>}
         */
        async handleDownloadLabelsForManifest(manifest) {
            const endpoint = `/api/carrier-jobs/manifests/${manifest.carrierJobManifestId}/labels`;
            try {
                const response = await handleRequests(endpoint, {
                    method: 'GET'
                });
                if (response.status === 200) {
                    /**
                     * @type {{ downloadUrl: string, carrierJobLabelId: number, orderId: number }}
                     */
                    const { labels } = response.data;
                    labels.forEach((label) => {
                        window.open(label.downloadUrl, '_blank');
                    });
                    this.$notify({
                        message: `Successfully opened ${labels.length} label(s) in new tab(s).`,
                        type: 'success'
                    });
                } else {
                    this.$notify({
                        message: 'Failed to download labels.',
                        type: 'error'
                    });
                }
            } catch (e) {
                const message = 'Error in downloading label';
                showErrorMessage(this, message, e);
            } finally {
                this.loadingOrders = this.loadingOrders.filter((id) => id !== orderId);
            }
        },
        handleBookNowClicked() {
            const orderIds = this.selectedOrders.map((x) => x.orderId);
            if (orderIds.length === 0) {
                return;
            }
            this.$modal.show(BookOrdersToCarrierModal, {
                orderIds,
                initialContactName: this.user.fullName,
                initialContactEmail: this.user.email,
                initialPhone: this.user.phone
            }).then((response) => {
                if (response && response.status === 'ok') {
                    // Get successful order IDs from the response data
                    // data is { orderId: number, success: boolean, message: string }
                    // loop through all items
                    // if it's successful, show message as success, remove from list
                    // else, show error message
                    // foreach order in data, if order.success, notify "Order {order.orderId} successfully booked to carrier." and remove from list
                    // else, notify "Order {order.orderId} failed to be booked to carrier: {order.message}"
                    const successfulOrders = response.data.filter((item) => item.success);
                    const failedOrders = response.data.filter((item) => !item.success);
                    // group the failed messages and show them in batch
                    if (failedOrders.length > 0) {
                        const failedMessages = failedOrders.map((item) => `OrderId: ${item.orderId} - ${item.message}`);
                        this.$notifyError(failedMessages.join('<br>'));
                    }
                    // group the success messages and show them in batch
                    if (successfulOrders.length > 0) {
                        const successMessages = successfulOrders.map((item) => `Order ${item.orderId} successfully booked to carrier.`);
                        this.$notify(successMessages.map(message => ({
                            message,
                            type: 'success',
                            duration: 5000
                        })));
                    }
                    // filter out successful orders from selected orders
                    this.selectedOrders = this.selectedOrders.filter((order) => !successfulOrders.some((x) => x.orderId === order.orderId));
                    this.orderList = this.orderList.filter((order) => !successfulOrders.some((x) => x.orderId === order.orderId));
                } else if (response && response.status === 'error') {
                    this.$modal.hide();
                    // Error notification is already handled in the modal
                }
            })


        },
        /**
         *
         * @param {string} tab
         */
        switchTab(tab) {
            this.activeTab = tab;
        },
        /**
         * Handle in preparation button click
         */
        async handleReadyForPackingClicked() {
            const orderIds = this.selectedOrders.map((x) => x.orderId);
            if (orderIds.length === 0) {
                return;
            }
            this.loadingOrders.push(...orderIds);
            try {
                const endpoint = '/api/carrier-jobs/set-in-preparation';
                const request = {
                    orderIds
                };
                const response = await handleRequests(endpoint, {
                    method: 'POST',
                    data: request
                });
                if (response.status === 200) {
                    const { data } = response;

                    // Count successful and failed orders
                    const successfulOrders = data.filter(item => item.success === true);
                    const failedOrders = data.filter(item => item.success === false);
                    const successCount = successfulOrders.length;
                    const failedCount = failedOrders.length;

                    // Get order IDs of successful orders
                    const successfulOrderIds = successfulOrders.map(item => item.orderId);

                    // Remove successful orders from orderList and selectedOrders
                    this.orderList = this.orderList.filter((x) => !successfulOrderIds.includes(x.orderId));
                    this.selectedOrders = this.selectedOrders.filter((order) => !successfulOrderIds.includes(order.orderId));

                    // Show notification with counts
                    const successNotifications = successfulOrders.map((item) => ({
                        message: `Order ${item.orderId} successfully set to in preparation.`,
                        type: 'success',
                        duration: 5000
                    }))
                    const failedNotifications = failedOrders.map((item) => ({
                        message: `Order ${item.orderId} failed to be set to in preparation: ${item.message}`,
                        type: 'danger',
                        duration: 5000
                    }))
                    if (successCount > 0) {
                        this.$notify(successNotifications);
                    }
                    if (failedCount > 0) {
                        this.$notify(failedNotifications);
                    }
                } else {
                    this.$notify({
                        message: 'Failed to set orders to in preparation.',
                        type: 'error'
                    });
                }
            } catch (e) {
                const message = 'Error in setting orders to in preparation';
                showErrorMessage(this, message, e);
            } finally {
                this.loadingOrders = this.loadingOrders.filter((id) => !orderIds.includes(id));
            }
        },
        /**
         *
         * @param {string} orderId
         */
        async handleDownloadLabelButtonClicked(orderId) {
            this.loadingOrders.push(orderId);
            const endpoint = `/api/carrier-jobs/labels?orderIds=${orderId}`;
            try {
                const response = await handleRequests(endpoint, {
                    method: 'GET'
                });
                if (response.status === 200) {
                    /**
                     * @type {{ downloadUrl: string, carrierJobLabelId: number, orderId: number }}
                     */
                    const { labels } = response.data;
                    // download the blob first
                    const { downloadUrl } = labels[0];
                    window.open(downloadUrl, '_blank');
                    this.$notify({
                        message: 'Label opened in new tab successfully.',
                        type: 'success'
                    });
                } else {
                    this.$notify({
                        message: 'Failed to download label.',
                        type: 'error'
                    });
                }
            } catch (e) {
                const message = 'Error in downloading label';
                showErrorMessage(this, message, e);
            } finally {
                this.loadingOrders = this.loadingOrders.filter((id) => id !== orderId);
            }
        },

        /**
         *
         * @returns {Promise<void>}
         */
        async getOrderServiceRates() {
            if (!this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace)) {
                return;
            }
            const orderIds = this.orderList.map((order) => order.orderId);
            if (orderIds.length === 0) {
                return;
            }
            this.orderRatesFetchStatus = 'pending';
            try {
                const data = await this.$_carrierMarketplace_getOrderServiceRates(orderIds);
                if (!data) {
                    return;
                }
                this.orderList = this.orderList.map((order) => {
                    const { orderServiceRates } = data;
                    const result = orderServiceRates.find((item) => item.orderId === order.orderId);
                    if (result) {
                        order.carrierServiceRates = result.serviceRates;
                        order.assignedCarrier = result.carrierJob;
                    } else {
                        order.carrierServiceRates = [];
                        order.assignedCarrier = null;
                    }
                    return order;
                });
            } catch (e) {
                const message = 'Error in fetching service rates';
                showErrorMessage(this, message, e);
            }
            this.orderRatesFetchStatus = 'idle';
        },
        /**
         *
         * @param {number} orderId
         * @returns {Promise<import("@/jsDocTypes/Carrier").CarrierJobDto>}
         */
        async fetchCarrierJob(orderId) {
            if (!this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace)) {
                return null;
            }
            const endpoint = `/api/carrier-jobs?orderId=${orderId}`;
            const response = await handleRequests(endpoint, {
                method: 'GET'
            });
            return response.data.carrierJob;
        },
        async handleBatchAssignClicked() {
            const selectedOrderIds = this.selectedOrders.map((order) => order.orderId);

            await this.$modal.show(AssignOrderToCarrierModal, {
                orderIds: selectedOrderIds
            });
            this.selectedOrders = [];
            // reload all records
            await this.fetchOrdersList();
        },
        /**
         *
         * @param {Object} eventData
         * @param {string} eventData.carrierServiceCode
         * @param {number} eventData.teamCarrierId
         * @param {string} eventData.carrierServiceName
         * @param {number[]} orderIds
         * @returns {Promise<"success" | "failed">}
         */
        async assignOrderToCarrier(eventData, orderIds) {
            const request = {
                orderIds,
                teamCarrierId: eventData.teamCarrierId,
                carrierServiceCode: eventData.carrierServiceCode,
                carrierServiceName: eventData.carrierServiceName
            };
            const endpoint = `/api/carrier-jobs/assign`;
            const response = await handleRequests(endpoint, {
                method: 'POST',
                data: request
            });
            if (response.status === 200) {
                return 'success';
            }
            return 'failed';
        },
        /**
         * @param {Object} params
         * @param {Object} params.eventData
         * @param {string} params.eventData.carrierServiceCode
         * @param {number} params.eventData.teamCarrierId
         * @param {string} params.eventData.carrierServiceName
         * @param {number} params.orderId
         */
        async handleAssignedCarrierChanged({ eventData, orderId }) {
            if (!this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace)) {
                return;
            }
            try {
                this.loadingOrders.push(orderId);
                const result = await this.assignOrderToCarrier(eventData, [orderId]);
                if (result !== 'success') {
                    this.$notify({
                        message: 'Failed to assign order to carrier.',
                        type: 'error'
                    });
                    return;
                }
                const carrierJob = await this.fetchCarrierJob(orderId);
                this.orderList = this.orderList.map((order) => {
                    if (order.orderId === orderId) {
                        order.assignedCarrier = carrierJob;
                    }
                    return order;
                });
                this.$notify({
                    message: 'Successfully assigned order to carrier.',
                    type: 'success'
                });
            } catch (e) {
                const message = 'Error in assigning order to carrier';
                showErrorMessage(this, message, e);
            }
            this.loadingOrders = this.loadingOrders.filter((id) => id !== orderId);
        },
        async fetchOrdersList(pageNumber = 1, itemsPerPage = 50) {
            if (this.fetchOrdersCancellationTokenSource) {
                this.fetchOrdersCancellationTokenSource.cancel('New request initiated');
            }
            this.fetchOrdersCancellationTokenSource = createCancellationTokenSource();
            try {
                this.isListLoading = true;
                const endpoint = `/api/orders/list`;
                const params = {
                    pageNumber,
                    itemsPerPage,
                    searchText: this.currentSearchTextValue,
                    dateFilterType: this.dateFilterType,
                    fromDate: this.fromDate,
                    toDate: this.toDate,
                    serviceAreaId: this.serviceAreaId,
                    customerId: this.customerId,
                    orderStatus: this.orderStatus,
                    rateGroupId: this.rateGroupId,
                    assignedToCarrier: this.showCarrierOrders
                };
                if (this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace)) {
                    params.carrierStatus = TABS.find((tab) => tab.label === this.activeTab).value;
                }
                const response = await handleRequests(
                    endpoint,
                    {
                        params
                    },
                    true,
                    this.fetchOrdersCancellationTokenSource.token
                );

                this.orderList = response.data.orders;
                this.pagination.total = response.data.totalOrders;
                this.pagination.perPage = itemsPerPage;
                this.isListLoading = false;
                if (this.showCarrierOrders) {
                    await this.getOrderServiceRates();
                }
            } catch (error) {
                const message = 'Error in fetching order list';
                showErrorMessage(this, message, error);
                this.isListLoading = false;
            }
            this.fetchOrdersCancellationTokenSource = null;
        },
        handleSelectedColumn(item) {
            this.selectedColumn = item;
        },
        async handleChangePage(currentPage = 1, perPage = 50) {
            this.pagination.currentPage = currentPage;
            this.pagination.perPage = perPage;
            await this.loadRecords({
                newTab: this.activeTab,
                currentPage,
                perPage
            });
            this.$router.replace({ path: this.$route.path, query: { currentPage } });
        },
        // eslint-disable-next-line func-names
        handleFilterOrSearch: _.debounce(function(val) {
            this.currentSearchTextValue = val.searchText;
            this.loadRecords({
                newTab: this.activeTab,
                currentPage: this.pagination.currentPage,
                perPage: this.pagination.perPage
            });
        }, 1000),
        handleFilterChange(filter) {
            this.customerId = filter.customerId;
            this.serviceAreaId = filter.serviceAreaId;
            this.dateFilterType = filter.filterType;
            this.fromDate = filter.fromDate;
            this.toDate = filter.toDate;
            this.selectedOrders = [];
            this.orderStatus = filter.orderStatus;
            this.rateGroupId = filter.rateGroupId;
            this.loadRecords({
                newTab: this.activeTab,
                currentPage: this.pagination.currentPage,
                perPage: this.pagination.perPage
            });
        },
        handleManifestFilterChange: _.debounce(function handler(filter) {
            this.manifestFromDate = filter.fromDate;
            this.manifestToDate = filter.toDate;
            this.selectedOrders = [];
            this.loadRecords({
                newTab: this.activeTab,
                currentPage: 1, // Reset to first page when filtering
                perPage: this.pagination.perPage
            });
        }, 500),
        onSelectAll(value) {
            if (value) {
                this.orderList.forEach((order) => {
                    const isSelected = this.selectedOrders.some(
                        (selectedOrder) => selectedOrder.orderId === order.orderId
                    );

                    if (isSelected) {
                        return;
                    }
                    this.selectedOrders.push(order);
                });
            } else {
                this.selectedOrders = [];
            }
        },
        singleCheckbox(order) {
            const isRemoved = !this.selectedOrders.some((selectedOrder) => selectedOrder.orderId === order.orderId);
            if (isRemoved) {
                this.isSelectAll = false;
            }
        },
        createHandleScroll() {
            document.querySelector('.main-panel').addEventListener('scroll', this.handleScroll, true);
        },
        handleScroll(e) {
            this.topScrollPosition = e.target.scrollTop;
        },
        sendSchedulingLink() {
            this.$modal
                .show(SendBulkSms, {
                    title: 'Send Bulk SMS',
                    selectedOrders: this.selectedOrders
                })
                .then((response) => {
                    if (response === 'ok')
                        this.$modal.hide();
                });
        },
        updateOrder(order) {
            this.$modal
                .show(UpdateOrderModal, {
                    orderId: order.orderId
                })
                .then((response) => {
                    if (response.code.toLowerCase() === 'ok') {
                        this.$modal.hide();
                        order.contact = response.data.contact;
                    }
                });
        },
        async handleDelete(order) {
            if (order)
                await this.fetchOrdersList();
        },
        async updateOrderStatus(order, action) {
            this.$_handleLoaderState(true, 'UPDATING...');
            const api = `/api/orders/${order.orderId}/${action}`;
            const payload = {
                method: 'post'
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully updated the status.',
                    type: 'success'
                });
                this.fetchOrdersList();
            } catch (e) {
                const message = 'Could not update order status.';
                showErrorMessage(this, message, e);
            }
            this.$_handleLoaderState(false);
        },
        putOrderOnHold(order) {
            let title = 'Order on Hold';
            let bodyMessage =
                'Are you sure you want to put this order on hold? This will also put the related stops to an on hold status.';
            if (order.status === 'On Hold') {
                title = 'Order Unhold';
                bodyMessage = 'Are you sure you want to unhold this order? This will also unhold the related stops.';
            }

            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title,
                    body: bodyMessage,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        let apiAction = 'on-hold';
                        if (order.status === 'On Hold') {
                            apiAction = 'unhold';
                        }
                        await this.updateOrderStatus(order, apiAction);
                    }
                });
        },
        cancelOrder(order) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Cancel Order',
                    body: `Are you sure you want to cancel this order? This can't be undone.`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        await this.updateOrderStatus(order, 'cancel');
                    }
                });
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .dropdown-menu {
    margin-left: 0 !important;
}

.custom-margin-bottom {
    margin-bottom: 0;
}

.custom-toolbar {
    .custom-toolbar-start {
        flex: 3;
    }
}

.table-bulk-action-container {
    top: -18px !important;
    z-index: 1000;
    .bulk-action-btn {
        width: 180px;
    }
}

.body-list {
    max-height: 70vh;
    overflow-y: auto;
    margin-top: 20px;
}

::v-deep .tab-content {
    margin-top: 0 !important;
}
</style>
